import type { Immobilisation, PlanAmortissementLigne } from '@shared/types/Immobilisation';
import { roundFCFA } from '@shared/utils/round';

function daysInclusive(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
  return Math.floor((b - a) / 86_400_000) + 1;
}

function maxIso(a: string, b: string): string {
  return a > b ? a : b;
}

function minIso(a: string, b: string): string {
  return a < b ? a : b;
}

/**
 * Service de calcul des amortissements OHADA.
 * - Linéaire : annuité constante = (VA - VR) / durée, avec prorata temporis année 1
 * - Dégressif : taux = taux_lineaire × coefficient (1,5 / 2 / 2,5 selon durée)
 */
export class AmortissementsService {
  /** Coefficient OHADA pour l'amortissement dégressif. */
  static coefficientDegressif(duree: number): number {
    if (duree <= 4) return 1.5;
    if (duree <= 6) return 2;
    return 2.5;
  }

  /**
   * Fraction d’année restante à compter de la date de mise en service (inclus).
   * 1er janvier → 1 ; 1er juillet → ~0,5.
   */
  static prorataAnnee1(dateMiseService: string): number {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateMiseService.trim());
    if (!m) return 1;
    const y = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (!y || month < 1 || month > 12 || day < 1 || day > 31) return 1;
    const start = Date.UTC(y, month - 1, day);
    const end = Date.UTC(y, 11, 31);
    const daysRemaining = Math.floor((end - start) / 86_400_000) + 1;
    const yearDays = (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? 366 : 365;
    return Math.min(1, Math.max(0, daysRemaining / yearDays));
  }

  /** Génère le plan d'amortissement complet. */
  static planAmortissement(immo: Immobilisation): PlanAmortissementLigne[] {
    const va = immo.valeur_acquisition;
    const vr = immo.valeur_residuelle ?? 0;
    const duree = immo.duree_amortissement_annees;
    const base = va - vr;

    if (duree <= 0 || base <= 0) return [];

    const prorata = this.prorataAnnee1(immo.date_mise_service || immo.date_acquisition);
    const plan: PlanAmortissementLigne[] = [];
    let cumul = 0;
    let vnc = va;
    const annuiteLineaire = roundFCFA(base / duree);

    if (immo.mode === 'lineaire') {
      for (let i = 1; i <= duree; i++) {
        let dotation: number;
        if (i === duree) {
          dotation = roundFCFA(va - vr - cumul);
        } else if (i === 1) {
          dotation = roundFCFA(annuiteLineaire * prorata);
        } else {
          dotation = annuiteLineaire;
        }
        cumul = roundFCFA(cumul + dotation);
        vnc = roundFCFA(vnc - dotation);
        plan.push({
          annee: i,
          valeur_debut: roundFCFA(vnc + dotation),
          dotation,
          cumul_amortissements: cumul,
          vnc,
        });
      }
    } else {
      const taux_lin = 1 / duree;
      const taux_deg = taux_lin * this.coefficientDegressif(duree);
      for (let i = 1; i <= duree; i++) {
        const annuiteDeg = roundFCFA(vnc * taux_deg * (i === 1 ? prorata : 1));
        const restant = duree - i + 1;
        const annuiteLinRestant = roundFCFA((vnc - vr) / restant);
        let dotation = Math.max(annuiteDeg, annuiteLinRestant);
        if (i === duree) dotation = roundFCFA(vnc - vr);
        cumul = roundFCFA(cumul + dotation);
        const ancien_vnc = vnc;
        vnc = roundFCFA(vnc - dotation);
        plan.push({
          annee: i,
          valeur_debut: ancien_vnc,
          dotation: roundFCFA(dotation),
          cumul_amortissements: cumul,
          vnc,
        });
      }
    }
    return this.tronquerSiCession(immo, plan);
  }

  /**
   * Fraction de l’exercice (dates d’ouverture / clôture) pendant laquelle le bien
   * est en service. Une année civile = 1er janvier → 31 décembre.
   */
  static prorataSurExercice(
    dateMiseService: string,
    debutExercice: string,
    finExercice: string,
    dateCession?: string,
  ): number {
    const start = maxIso(dateMiseService, debutExercice);
    const end = minIso(dateCession ?? finExercice, finExercice);
    if (!start || !end || end < start) return 0;
    const denom = daysInclusive(debutExercice, finExercice);
    if (denom <= 0) return 0;
    return Math.min(1, Math.max(0, daysInclusive(start, end) / denom));
  }

  /** Arrête le plan à la date de cession et proratise la dernière année. */
  static tronquerSiCession(
    immo: Immobilisation,
    plan: PlanAmortissementLigne[],
  ): PlanAmortissementLigne[] {
    if (!immo.cede || !immo.date_cession || plan.length === 0) return plan;
    const debut = Number((immo.date_mise_service || immo.date_acquisition).slice(0, 4));
    const anneeCession = Number(immo.date_cession.slice(0, 4));
    if (!debut || !anneeCession) return plan;
    const rangMax = anneeCession - debut + 1;
    if (rangMax <= 0) return [];
    const truncated = plan.slice(0, rangMax).map((l) => ({ ...l }));
    const last = truncated[truncated.length - 1];
    if (!last) return [];
    const yearStart =
      rangMax === 1
        ? immo.date_mise_service || immo.date_acquisition
        : `${anneeCession}-01-01`;
    const yearEnd = `${anneeCession}-12-31`;
    const factor = this.prorataSurExercice(
      yearStart,
      yearStart,
      yearEnd,
      immo.date_cession,
    );
    const oldDot = last.dotation;
    last.dotation = roundFCFA(oldDot * factor);
    last.cumul_amortissements = roundFCFA(last.cumul_amortissements - oldDot + last.dotation);
    last.vnc = roundFCFA(last.valeur_debut - last.dotation);
    return truncated;
  }

  /** Renvoie la dotation de l'exercice donné (1-indexée — rang du plan). */
  static dotationExercice(immo: Immobilisation, annee_exercice: number): number {
    const plan = this.planAmortissement(immo);
    const ligne = plan[annee_exercice - 1];
    return ligne?.dotation ?? 0;
  }

  /** Dotation pour une année civile (l’appelant passe 2026, pas le rang 1). */
  static dotationPourAnneeCivile(immo: Immobilisation, annee: number): number {
    const debut = Number((immo.date_mise_service || immo.date_acquisition).slice(0, 4));
    if (!debut || annee < debut) return 0;
    return this.dotationExercice(immo, annee - debut + 1);
  }

  /** Renvoie la VNC à la fin de l'exercice donné. */
  static vncFinExercice(immo: Immobilisation, annee_exercice: number): number {
    const plan = this.planAmortissement(immo);
    const ligne = plan[annee_exercice - 1];
    return ligne?.vnc ?? immo.valeur_acquisition;
  }
}
